"use client";

import React, { useState } from "react";
import Heading from "../Heading";
import ChipsBlock from "./ChipsBlock";

interface ChipItem {
	href: string;
	title: string;
	label: string;
}

interface TabConfig {
	label: string;
	chips: ChipItem[];
}

interface ChipsTabSectionProps {
	heading: string;
	tabs: TabConfig[];
	trackingClass?: string;
	reducedPadding?: boolean;
}

const ChipsTabSection: React.FC<ChipsTabSectionProps> = ({
	heading,
	tabs,
	trackingClass,
	reducedPadding = false,
}) => {
	const availableTabs = tabs.filter((tab) => tab.chips?.length > 0);
	const [activeTab, setActiveTab] = useState(0);

	if (!availableTabs.length) {
		return null;
	}

	const colClassName =
		"flex-none px-2 w-auto min-w-[120px] max-w-none max-[991px]:w-1/2 max-[991px]:min-w-[50%]";
	const paddingClasses = reducedPadding
		? "py-1 px-8 max-[991px]:px-[18px]"
		: "py-2 px-8 max-[991px]:py-1 max-[991px]:px-4 min-[420px]:max-[480px]:px-3";
	const blockClassName = `text-center text-[14px] leading-[32px] my-[3px] rounded-[7px] !font-medium text-[#6f6f6f] border border-[#6f6f6f] hover:text-[#008c8b] hover:bg-[#008c8b0d] hover:border-[0.4px] hover:border-[#008c8b] ${paddingClasses} max-[991px]:overflow-hidden max-[991px]:text-ellipsis max-[991px]:cursor-pointer max-[991px]:text-[13px] min-[420px]:max-[480px]:text-[12px]`;

	return (
		<div className="p-3 md:p-4 bg-white rounded-[14px] border border-gray-200">
			<Heading tag="h2" className="font-bold">
				{heading}
			</Heading>

			{availableTabs.length > 1 && (
				<div className="flex items-center gap-3 border-b border-gray-200 min-h-[42px] mt-3">
					<div className="min-w-0 flex-1 overflow-x-auto scrollbar-hide -mb-px">
						<div className="flex whitespace-nowrap">
							{availableTabs.map((tab, index) => (
								<button
									key={tab.label}
									type="button"
									onClick={() => setActiveTab(index)}
									className={`shrink-0 px-1 pr-5 py-2.5 font-medium outline-none focus:outline-none focus:ring-0 cursor-pointer text-[16px] md:text-[13px] lg:text-[16px] ${
										activeTab === index
											? "border-b-2 border-[#008c8b] text-[#008c8b]"
											: "text-gray-500 hover:text-black border-b-2 border-transparent"
									}`}
								>
									{tab.label}
								</button>
							))}
						</div>
					</div>
				</div>
			)}

			{availableTabs.map((tab, tabIndex) => (
				<div
					key={tab.label}
					className={`${activeTab === tabIndex ? "block" : "hidden"} ${availableTabs.length > 1 ? "mt-4" : "mt-3"}`}
				>
					<div className="flex flex-wrap -mx-2 gap-y-4 gap-x-0 cj-row">
						{tab.chips.map((chip, index) => (
							<ChipsBlock
								key={`${chip.href}-${index}`}
								href={chip.href}
								title={chip.title}
								label={chip.label}
								trackingClass={trackingClass}
								colClassName={colClassName}
								blockClassName={blockClassName}
							/>
						))}
					</div>
				</div>
			))}
		</div>
	);
};

export default ChipsTabSection;
